import { useGetInterfaceStoreState } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import type { EmitEventType, TriggerEventParams } from '@unifyapps/defs/hooks/useBlockEvents';
import type { BlockId } from '@unifyapps/defs/types/block';
import type { Event } from '@unifyapps/defs/types/event';
import { useLatest } from '@unifyapps/hooks/useLatest';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useActionsContext } from '../../context/ActionsProvider';
import { useBlockEventsGateway } from '../../context/BlockEventsGateway';
import { usePageInstanceId } from '../../context/PageInstanceIdProvider';
import { NoCodeEventHandler } from '../../event';
import { createEventPulseObserver } from '../../pulse/createEventPulseObserver';
import { APP_MANAGER_ID, scopeIdOf } from '../../pulse/scopeRegistry';
import { GlobalStateStoreManagerContext } from '../../stores/GlobalStateStore/GlobalStateStoreProvider';
import { getOriginalBlockId } from '../../utils/runTimeBlocks';
import { useGetAppComputeContext, useGetComputeContext } from '../computeContext';
import { useEvaluateConditionalOperator } from '../useComputeConditionEvaluationGetter';
import { useCanTriggerEvents } from './useCanTriggerEvents';

interface updateCapturedEmitParamsForPanicClickProps {
  blockId: BlockId;
  emitParams: Omit<TriggerEventParams, 'instanceId' | 'doAction'>;
}

function useBaseBlockEvents({
  events,
  blockId,
  getComputeContext,
  instanceId,
  managerId,
  updateCapturedEmitParamsForPanicClick,
}: {
  events?: Event[];
  blockId?: BlockId;
  getComputeContext: () => Record<string, unknown>;
  instanceId?: string;
  // The caller (page or app wrapper) resolves the manager id from its own context and
  // supplies it so its events tag the correct scope.
  managerId?: string;
  updateCapturedEmitParamsForPanicClick?: ({
    blockId,
    emitParams,
  }: updateCapturedEmitParamsForPanicClickProps) => boolean;
}) {
  const { evaluateConditionalOperator } = useEvaluateConditionalOperator();
  const { doAction } = useActionsContext();
  const { onAddWaitingEventCallback, filterWaitingEvents } = useBlockEventsGateway();
  // Pulse scope source: the caller (useBlockEvents / useAppBlockEvents) resolves the
  // manager id from its context and passes it in; this hook stays context-agnostic.
  const resolveScope = useCallback(
    (eventInstanceId?: string) => scopeIdOf(managerId ?? 'unknown', eventInstanceId ?? null),
    [managerId],
  );

  // Handler is cached for the hook's lifetime, but getComputeContext closes over
  // blocksRuntimeState and gets a new closure whenever a carousel slide or
  // repeatable row re-renders. Without the refs, runCondition would keep reading
  // the first-render context (carousel repro: edited mapped variable was ignored).
  const { ref: onAddWaitingEventCallbackRef } = useLatest(onAddWaitingEventCallback);
  const { ref: filterWaitingEventsRef } = useLatest(filterWaitingEvents);
  const { ref: getComputeContextRef } = useLatest(getComputeContext);
  const { ref: evaluateConditionalOperatorRef } = useLatest(evaluateConditionalOperator);
  const { ref: resolveScopeRef } = useLatest(resolveScope);
  const eventHandlerRef = useRef<NoCodeEventHandler | null>(null);

  const getEventHandler = useCallback(() => {
    if (eventHandlerRef.current) return eventHandlerRef.current;
    // Arrows captured once; bodies deref .current at call time so dispatch always
    // sees the latest closure.
    eventHandlerRef.current = new NoCodeEventHandler({
      getComputeContext: () => getComputeContextRef.current(),
      evaluateConditionalOperator: (params) => evaluateConditionalOperatorRef.current(params),
      onAddWaitingEventCallback: (eventId) => onAddWaitingEventCallbackRef.current?.(eventId),
      filterWaitingEvents: (events) =>
        filterWaitingEventsRef.current ? filterWaitingEventsRef.current(events) : [],
      // Pulse tracking plugs in from outside; the handler itself stays Pulse-agnostic. The ref
      // keeps scope resolution reading the latest manager context across re-renders.
      observer: createEventPulseObserver((eventInstanceId) =>
        resolveScopeRef.current(eventInstanceId),
      ),
    });
    return eventHandlerRef.current;
  }, [
    getComputeContextRef,
    evaluateConditionalOperatorRef,
    onAddWaitingEventCallbackRef,
    filterWaitingEventsRef,
    resolveScopeRef,
  ]);

  useMemo(() => {
    // we do not expect events to change in runner or preview mode so we reset the event handler
    // when events change that is mostly when events are changed in builder mode
    getEventHandler().reset();
    getEventHandler().registerEvents(events || []);
  }, [events, getEventHandler]);

  useCanTriggerEvents(getEventHandler);

  const emitEvent = useCallback<EmitEventType>(
    async (args) => {
      // for the panic click feature, when the user clicks a block twice very quickly,
      // we allow the third click to pass through and trigger the event. This lets us
      // store the params in the interface store, which will later be used to emit the
      // event once the user confirms the action in the panic click extension component.
      if (updateCapturedEmitParamsForPanicClick && blockId) {
        const areParamsCaptured = updateCapturedEmitParamsForPanicClick({
          blockId,
          emitParams: args,
        });
        if (areParamsCaptured) return;
      }

      const {
        eventType,
        domEvent,
        actionContext,
        targetId,
        anchorId,
        eventPayload,
        eventId,
        label,
      } = args;
      if (doAction) {
        await getEventHandler().triggerEvent({
          eventType,
          doAction,
          actionContext,
          domEvent,
          targetId,
          anchorId,
          blockId,
          instanceId,
          eventId,
          eventPayload,
          label,
        });
      } else {
        console.warn('doAction is not defined but event is being triggered', {
          eventType,
          domEvent,
          actionContext,
          targetId,
          blockId,
        });
      }
    },
    [blockId, instanceId, doAction, getEventHandler, updateCapturedEmitParamsForPanicClick],
  );

  return { emitEvent };
}

function useBlockEvents(events?: Event[], blockId?: BlockId) {
  const { getComputeContext } = useGetComputeContext({
    mode: 'mutable',
  });
  const instanceId = usePageInstanceId();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  // Page Pulse scope source: read the page manager context here (not inside the base hook)
  // and pass it down, so the base hook stays context-agnostic.
  const managerId = useContext(GlobalStateStoreManagerContext)?.managerId ?? 'unknown';

  const updateCapturedEmitParamsForPanicClick = useCallback(
    ({ blockId, emitParams }: updateCapturedEmitParamsForPanicClickProps) => {
      if (blockId) {
        const normalizedBlockId = getOriginalBlockId(blockId);
        if (getInterfaceStoreState().panicClick.awaitingBlockId === normalizedBlockId) {
          getInterfaceStoreState().actions.panicClickActions({
            operation: 'SET_CAPTURED_PARAMS',
            payload: {
              blockId: normalizedBlockId,
              params: emitParams,
            },
          });
          return true;
        }
      }

      return false;
    },
    [getInterfaceStoreState],
  );

  return useBaseBlockEvents({
    events,
    blockId,
    getComputeContext,
    instanceId,
    managerId,
    updateCapturedEmitParamsForPanicClick,
  });
}

function useAppBlockEvents(events?: Event[], blockId?: BlockId) {
  // No rowScopeStack: this hook is consumed by AppDataSourceController, which
  // mounts above any Repeatable. App data sources have no per-row identity.
  const { getAppComputeContext } = useGetAppComputeContext({
    mode: 'mutable',
  });
  const managerId = APP_MANAGER_ID;

  return useBaseBlockEvents({
    events,
    blockId,
    getComputeContext: getAppComputeContext,
    managerId,
  });
}

export { useAppBlockEvents, useBlockEvents as default };
